import { Component, OnInit, AfterViewInit, Output, EventEmitter, ViewChild, TemplateRef, OnDestroy, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { finalize, take, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AgreeComponent } from '../shared/agree/agree.component';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { UtilsService } from 'src/app/services/utils.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ChatbotOverlayService } from 'src/app/services/chatbot-overlay.service';
import * as moment from 'moment-timezone';
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import flatpickr from 'flatpickr';
import { ChatBotHelperService } from './chat-bot-helper.service';
@Component({
  selector: 'app-chat-bot',
  templateUrl: './chat-bot.component.html',
  styleUrls: ['./chat-bot.component.scss'],
})
export class ChatBotComponent implements OnInit, AfterViewInit, OnDestroy {
  isMinimized = false;
  isChatVisible = true;

  defaultMsg: any = { sender: 'assistant', message: 'Hi I’m Iris. How can I help you today?', timestamp: '', formatTime: '' };
  errMsg: any = { sender: 'assistant', message: 'Sorry, something went wrong. Please try again.', timestamp: '', formatTime: '' };

  messages: any[] = [];
  draftMessage: any[] = [];
  selectedOptions: { [key: number]: Array<{ label: string; value: string }> } = {};
  selectedTableRows: { [key: number]: Array<any> } = {};
  pendingEulaMessages: any;
  currentUser: any;
  sessionId: any;
  userMessage: string = '';
  userTimezone: string = moment.tz.guess();

  loading: boolean = false;
  disableMail: boolean = false;
  // Awaiting / retry state for retryable 500 responses
  isAwaiting: boolean = false;
  awaitingRetryTimer: any = null;
  pendingRetryPayload: any = null;
  pendingRetryDisplayText: any = null;
  pendingRetryAttempts = 0;
  readonly AWAITING_RETRY_DELAY_MS = 40000; // 40 seconds
  readonly AWAITING_RETRY_SUBSEQUENT_DELAY_MS = 20000; // 20 seconds
  readonly MAX_AWAITING_RETRIES = 3;
  isExpanded = false;
  showCloseConfirm    = false;
  sendingMailToUser   = false;
  mailSentToUser      = false;  
  profileDefaultTime: string = '00:00';

  @Output() closeChatWindow = new EventEmitter<any>();

  @ViewChild('successDialog') successDialog!: TemplateRef<any>;
  @ViewChildren('fpInput') fpInputs?: QueryList<ElementRef<HTMLInputElement>>;
  userImg: any;
  chatForm!: FormGroup;
  private flatpickrInstances = new Map<HTMLInputElement, any>();
  private boundFlatpickrInputs = new WeakSet<HTMLInputElement>();
  private markdownCellCache = new Map<string, SafeHtml>();

  todayDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  dateRangeStart: string = '';
  dateRangeEnd: string = '';

  scheduleDate: string = '';
  recurrentDate: string = '';
  recurrentStartDate: string = '';
  recurrentEndDate: string = '';
  periodStartDate: string = '';
  periodEndDate: string = '';

  scheduleStartTime: string = '';
  scheduleEndTime: string = '';
  scheduleStartSelection: string = '';
  scheduleEndSelection: string = '';

  recRepeatFrequency: string = 'weekly';
  recSubdailyCount: number = 1;
  activeScheduleMode: 'specific_date' | 'specific_day' | 'recurrent' | 'period' | null = null;
  submitError: string = '';
  activeInteractiveMessageIndex: number = -1;
  private activeChatSubscription?: Subscription;
  private restoredFromState = false;
  weekDays = [
  { label: 'Sunday', value: 'sunday' },
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
];

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private dialog: MatDialog,
    private userService: UserService,
    private utilsService: UtilsService,
    private router: Router,
    private chatbotOverlay: ChatbotOverlayService,
    private sanitizer: DomSanitizer,
    private chatBotHelper: ChatBotHelperService,
  ) {}

  ngOnInit(): void {
    this.chatForm = new FormGroup({
      message: new FormControl('', [Validators.required, Validators.minLength(1)])
    });

    this.authService.user.pipe(take(1)).subscribe((user) => {
      this.currentUser = user;
      const profileTimezone = this.currentUser?.timezone || this.utilsService.getTimezoneFromCountry(this.currentUser?.country);
      this.userTimezone = this.isValidTimezone(profileTimezone) ? profileTimezone : moment.tz.guess();
      this.profileDefaultTime = this.getProfileDefaultTimeHHmm();

      this.userService.personalInfo(this.currentUser?.id).pipe(take(1)).subscribe({
        next: (res: any) => {
          if (res?.photo !== undefined) {
            this.userImg = environment.usersServer + '/data/' + res.photo;
          }
        },
        error: () => {}
      });

      const shouldRestore = this.chatbotOverlay.consumeRestoreOnNextOpen();
      const savedState = shouldRestore ? this.chatbotOverlay.getChatState() : null;
      if (savedState?.messages?.length) {
        this.isMinimized = savedState.isMinimized ?? false;
        this.sessionId = savedState.sessionId ?? '';
        marked.setOptions({ breaks: true });
        this.messages = savedState.messages.map((m: any) => ({
          ...m,
          timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
          messageHtml: (m.sender === 'assistant' && m.message)
            ? this.sanitizer.bypassSecurityTrustHtml(marked.parse(m.message) as string)
            : undefined
        }));
        this.draftMessage = [...this.messages];
        this.restoredFromState = true;
      } else {
        this.chatbotOverlay.clearChatState();
        const msg = { ...this.defaultMsg, timestamp: new Date(), formatTime: this.convertTime(new Date()) };
        this.messages = [];
        this.draftMessage = [msg];
      }
      this.loading = false;
    });

    window.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mouseup', this.handleMouseUp);
  }

  private handleAssistantResponse(response: any) {
    this.sessionId = response?.session_id;
    const assistantPayload = this.normalizeAssistantResponse(response);
    const intentType = response?.response?.intent_type || assistantPayload.intent || null;
    const assistantMessageState = this.createAssistantMessageState(assistantPayload.uiType, assistantPayload.intent);

    this.messages.push({
      sender: 'assistant',
      message: assistantPayload.verbal,
      messageHtml: assistantPayload.verbalHtml,
      ui: assistantPayload.ui,
      uiType: assistantPayload.uiType,
      intent: assistantPayload.intent || null,
      intentType: intentType,
      uiState: assistantMessageState,
      timestamp: new Date(),
      formatTime: this.convertTime(new Date())
    });
    this.draftMessage.push({
      sender: 'assistant',
      message: assistantPayload.verbal,
      messageHtml: assistantPayload.verbalHtml,
      timestamp: new Date(),
      formatTime: this.convertTime(new Date()),
      ui: assistantPayload.ui,
      uiType: assistantPayload.uiType,
      intent_type: intentType
    });

    if (intentType === 'EULA_PAYMENT') {
      const msgIndex = this.messages.length - 1;
      this.handleEulaIntentForMessage(assistantPayload, msgIndex);
    }
    if (assistantPayload?.ui) {
      this.activeInteractiveMessageIndex = this.messages.length - 1;
    }

    // clear awaiting state if any
    this.loading = false;
    this.isAwaiting = false;
    this.pendingRetryPayload = null;
    this.pendingRetryDisplayText = null;
    this.pendingRetryAttempts = 0;
    if (this.awaitingRetryTimer) {
      clearTimeout(this.awaitingRetryTimer);
      this.awaitingRetryTimer = null;
    }

    this.scrollToBottom(100);
    this.saveState();
  }

  private startAwaitingRetry(payload: any, displayText?: any, resetAttempts: boolean = false) {
    // store payload/display for retry
    this.pendingRetryPayload = payload;
    this.pendingRetryDisplayText = displayText;
    if (resetAttempts) {
      this.pendingRetryAttempts = 0;
    }

    // set UI state (keep controls locked)
    this.isAwaiting = true;
    this.loading = true;
    this.disableMail = true;

    // schedule retry
    if (this.awaitingRetryTimer) {
      clearTimeout(this.awaitingRetryTimer);
      this.awaitingRetryTimer = null;
    }

    const delay = this.pendingRetryAttempts === 0 ? this.AWAITING_RETRY_DELAY_MS : this.AWAITING_RETRY_SUBSEQUENT_DELAY_MS;

    this.awaitingRetryTimer = setTimeout(() => {
      this.performPendingRetry();
    }, delay);
  }

  private performPendingRetry() {
    if (!this.pendingRetryPayload) {
      this.isAwaiting = false;
      this.loading = false;
      this.disableMail = false;
      return;
    }

    this.pendingRetryAttempts++;

    if (this.pendingRetryAttempts > this.MAX_AWAITING_RETRIES) {
      this.clearAwaitingState();
      const errMsg = { ...this.errMsg, timestamp: new Date(), formatTime: this.convertTime(new Date()) };
      this.messages.push(errMsg);
      this.draftMessage.push(errMsg);
      this.scrollToBottom(100);
      this.saveState();
      return;
    }

    this.activeChatSubscription = this.apiService.sendUserMessage(this.pendingRetryPayload)
      .pipe(finalize(() => {
        // awaiting logic handles loading/unlock
      }))
      .subscribe({
        next: (response) => {
          if (response?.response?.metadata?.error === 'model_not_ready') {
            if (this.pendingRetryAttempts >= this.MAX_AWAITING_RETRIES) {
              this.clearAwaitingState();
              const errMsg = { ...this.errMsg, timestamp: new Date(), formatTime: this.convertTime(new Date()) };
              this.messages.push(errMsg);
              this.draftMessage.push(errMsg);
              this.scrollToBottom(100);
              this.saveState();
            } else {
              this.startAwaitingRetry(this.pendingRetryPayload, this.pendingRetryDisplayText, false);
            }
          } else {
            this.handleAssistantResponse(response);
          }
        },
        error: (err) => {
          if (this.pendingRetryAttempts >= this.MAX_AWAITING_RETRIES) {
            this.clearAwaitingState();
            const errMsg = { ...this.errMsg, timestamp: new Date(), formatTime: this.convertTime(new Date()) };
            this.messages.push(errMsg);
            this.draftMessage.push(errMsg);
            this.saveState();
          } else {
            this.startAwaitingRetry(this.pendingRetryPayload, this.pendingRetryDisplayText, false);
          }
          this.scrollToBottom(100);
        }
      });
  }

  ngOnDestroy(): void {
    this.activeChatSubscription?.unsubscribe();
    this.clearAwaitingState();

    this.loading = false;
    window.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('mouseup', this.handleMouseUp);
    this.flatpickrInstances.forEach((instance) => instance?.destroy?.());
    this.flatpickrInstances.clear();
    if (this.awaitingRetryTimer) {
      clearTimeout(this.awaitingRetryTimer);
      this.awaitingRetryTimer = null;
    }
  }

  ngAfterViewInit(): void {
    if (this.restoredFromState) {
      this.restoredFromState = false;
      setTimeout(() => this.scrollToBottom(100), 150);
    }
  }

  ngAfterViewChecked(): void {
    this.bindFlatpickrInputs();
  }

  handleMouseDown = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest('.flatpickr-calendar')) return;
    const isDragHandle = !!target.closest('[cdkDragHandle]');
    if (!isDragHandle) return;
    document.body.classList.add('chat-resizing');
  };

  handleMouseUp = () => {
    document.body.classList.remove('chat-resizing');
  };

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  goToTerms() {
    if (this.router.url !== '/termsofuse') {
      this.router.navigate(['/termsofuse']);
    }
  }

  onEnter(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.ctrlKey) return;
    event.preventDefault();
    event.stopPropagation();
    this.sendMessage(event);
  }

  sendMessage(event: any) {
    if (event) event.preventDefault();
    if (this.chatForm.invalid || this.loading || this.messages[this.messages.length - 1]?.disableUserInput) return;

    const messageText = this.chatForm.value.message.trim();
    if (!messageText) return;

    this.sendMessageToChatbot(messageText, messageText);
    this.chatForm.get('message')?.setValue('');
  }

  sendMessageToChatbot(messageText: any, displayText?: string) {
    const now = new Date();
    const serializedMessage = typeof messageText === 'string' ? messageText : JSON.stringify(messageText);
    const userVisibleMessage = displayText ?? serializedMessage;

    this.messages.push({ sender: 'user', message: userVisibleMessage, timestamp: now, formatTime: this.convertTime(now) });
    this.draftMessage.push({ sender: 'user', message: userVisibleMessage, timestamp: now, formatTime: this.convertTime(now) });
    this.saveState();
    this.clearSubmitError();
    this.loading = true;
    this.scrollToBottom(100);

    const payload = {
      user_id: this.currentUser?.id,
      session_id: this.sessionId || '',
      message: serializedMessage
    };

    this.activeChatSubscription = this.apiService.sendUserMessage(payload)
      .pipe(finalize(() => {
        // do not clear loading here if we're awaiting a retry
        if (!this.isAwaiting) {
          this.loading = false;
          this.disableMail = false;
        }
      }))
      .subscribe({
        next: (response) => {
          // handle via centralized assistant response handler
          if(response?.response?.metadata?.error === 'model_not_ready'){
            this.startAwaitingRetry(payload, userVisibleMessage, true);
          }
          else{
            this.handleAssistantResponse(response);
          }
        },
        error: (err: any) => {
          this.startAwaitingRetry(payload, userVisibleMessage, true);
          this.scrollToBottom(100);
        }
      });
  }

  private createAssistantMessageState(uiType: string | null, intent?: string | null): any {
    return {
      uiType,
      intent: intent || null,
      isMessageCompleted: false,
      isFormSubmitted: false,
      submitError: '',
      activeScheduleMode: null,
      dateRangeStart: '',
      dateRangeEnd: '',
      scheduleDate: '',
      recurrentDate: '',
      recurrentStartDate: '',
      recurrentEndDate: '',
      periodStartDate: '',
      periodEndDate: '',
      scheduleStartTime: '',
      scheduleEndTime: '',
      scheduleStartSelection: '',
      scheduleEndSelection: '',
      isFormValid: false,
      recRepeatFrequency: 'weekly',
      recSubdailyCount: 1,
      specificDay: '',
    };
  }

  formatDateForDisplayShort(dateValue: string): string {
    if (!dateValue) return '';
    const m = moment(dateValue, 'YYYY-MM-DD', true);
    if (!m.isValid()) return dateValue;
    return m.format('DD-MM-YYYY');
  }

  formatOptionLabel(label: string | null | undefined): string {
    if (!label) return '';

    const s = String(label);
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  private validateScheduleState(messageIndex: number): boolean {
    const state = this.getMessageUiState(messageIndex);
    if (!state) return true;

    // Clear previous error
    state.submitError = '';
    let startDate = '';
    let endDate = '';
    let startTimeSel = state.scheduleStartSelection || '';
    let endTimeSel = state.scheduleEndSelection || '';

    // Determine mode-specific dates
    if (state.activeScheduleMode === 'specific_date') {
      startDate = state.scheduleDate;
      endDate = state.scheduleDate;
    }else if (state.activeScheduleMode === 'specific_day') {
      if (!state.specificDay) { 
        state.submitError = 'Please select a day.';
        state.isFormValid = false;
        return false;
      }
      startDate = moment.tz(this.userTimezone).day(state.specificDay).format('YYYY-MM-DD');
      endDate = startDate;
    } else if (state.activeScheduleMode === 'recurrent') {
      startDate = state.recurrentStartDate;
      endDate = state.recurrentEndDate;
    } else if (state.activeScheduleMode === 'period') {
      startDate = state.periodStartDate;
      endDate = state.periodEndDate;
    } else if (state.activeScheduleMode === 'recurrent' && state.recRepeatFrequency === 'subdaily' && Number(state.recSubdailyCount) > 8) {
      state.submitError = 'Subdaily Count cannot exceed 8.';
      state.isFormValid = false;
      return false;
    }
     else {
      startDate = state.dateRangeStart || '';
      endDate = state.dateRangeEnd || '';
    }

    // Basic presence checks for date_range
    if (!startDate || !endDate) {
      state.isFormValid = false;
      return false;
    }

    // Ensure end date is not before start date
    const startMomentDate = moment(startDate, 'YYYY-MM-DD', true);
    const endMomentDate = moment(endDate, 'YYYY-MM-DD', true);
    if (!startMomentDate.isValid() || !endMomentDate.isValid() || endMomentDate.isBefore(startMomentDate)) {
      state.submitError = 'End date must be on or after start date.';
      state.isFormValid = false;
      return false;
    }

    // For schedule modes that require times, ensure times present
    if (state.activeScheduleMode === 'specific_date' || state.activeScheduleMode === 'specific_day' || state.activeScheduleMode === 'recurrent' || state.activeScheduleMode === 'period') {
      if (!startTimeSel || !endTimeSel) {
        state.submitError = 'Please select both start and end times.';
        state.isFormValid = false;
        return false;
      }

      // Parse payload-friendly times (HH:mm)
      const startTimeForPayload = this.formatTimeForPayload(startTimeSel);
      const endTimeForPayload = this.formatTimeForPayload(endTimeSel);
      if (!startTimeForPayload || !endTimeForPayload) {
        state.submitError = 'Please select valid times.';
        state.isFormValid = false;
        return false;
      }

      // Build full moments in user's timezone
      const startLocal = moment.tz(`${startDate} ${startTimeForPayload}`, 'YYYY-MM-DD HH:mm', this.userTimezone);
      const endLocal = moment.tz(`${endDate} ${endTimeForPayload}`, 'YYYY-MM-DD HH:mm', this.userTimezone);

      if (!startLocal.isValid() || !endLocal.isValid()) {
        state.submitError = 'Please select valid start and end times.';
        state.isFormValid = false;
        return false;
      }

      // Start time restriction for today's date
      const nowLocal = moment.tz(this.userTimezone);
      const selectedStartDateLocal = startLocal.clone().startOf('day');
      if (selectedStartDateLocal.isSame(nowLocal.clone().startOf('day'))) {
        if (!startLocal.isAfter(nowLocal)) {
          state.submitError = 'The start time needs to be ahead of the current time.';
          state.isFormValid = false;
          return false;
        }
      }

      // Ensure at least 4-hour gap between start and end
      const diffHours = endLocal.diff(startLocal, 'hours', true);
      if (diffHours < 4) {
        state.submitError = 'End time must be at least 4 hours after the start time.';
        state.isFormValid = false;
        return false;
      }
    }

    state.isFormValid = true;
    state.submitError = '';
    return true;
  }

  private getMessageUiState(messageIndex: number): any {
    return this.messages?.[messageIndex]?.uiState || null;
  }

  private lockMessageUiState(messageIndex: number): void {
    const state = this.getMessageUiState(messageIndex);
    if (!state) return;

    state.isFormSubmitted = true;
    state.isMessageCompleted = true;
    state.submitError = '';
  }

  private setMessageSubmitError(messageIndex: number, message: string): void {
    const state = this.getMessageUiState(messageIndex);
    if (!state) return;
    state.submitError = message;
  }

  clearMessageSubmitError(messageIndex: number): void {
    const state = this.getMessageUiState(messageIndex);
    if (!state) return;
    state.submitError = '';
  }

  isMessageDisabled(msg: any, isLast: boolean): boolean {

    if (this.loading) {
      return true;
    }

    if (msg.sender === 'assistant' && msg.uiType) {
      return !isLast;
    }

    return false;
  }

  normalizeAssistantResponse(response: any) {
    const envelope = response?.response || null;

    const verbalRaw =
      typeof envelope?.verbal === 'string'
        ? envelope.verbal
        : typeof response?.final_answer === 'string'
        ? response.final_answer
        : typeof response?.message === 'string'
        ? response.message
        : '';

    const verbalText =
      typeof verbalRaw === 'string' ? verbalRaw.replace(/\\n/g, '\n') : '';

    let verbalHtml: SafeHtml = '';

    try {
      marked.setOptions({ breaks: true }); // enables line breaks
      const rawHtml: any = marked.parse(verbalText);
      verbalHtml = this.sanitizer.bypassSecurityTrustHtml(rawHtml);
    } catch (e) {
      verbalHtml = this.sanitizer.bypassSecurityTrustHtml(verbalText);
    }

    return {
      verbal: verbalText,
      verbalHtml,
      ui: envelope?.ui || null,
      uiType: envelope?.ui?.type || null,
      intent: envelope?.intent || null,
      metadata: envelope?.metadata || null
    };
  }

  // Initialize pending EULA entries for an assistant message (reads ui.data / metadata)
  handleEulaIntentForMessage(messagePayload: any, msgIndex: number) {

    if (!this.pendingEulaMessages) {
      this.pendingEulaMessages = [];
    }

    const dataItems =
      messagePayload?.ui?.data ||
      messagePayload?.data ||
      messagePayload?.metadata?.data ||
      [];

    const items = (dataItems as any[]).map((it) => ({
      label: it.label,
      value: it.value,
      payment_details_eula: it.payment_details_eula || it.paymentDetailsEula || it.payment_details || {},
      enabled: false
    }));
    this.pendingEulaMessages[msgIndex] = { items, accepted: false };
  }

  // Open the existing AgreeComponent dialog (reused from Payment flow) for a specific item
  openEulaFromChat(msgIndex: number, itemIndex: number = 0) {
    const entry = this.pendingEulaMessages[msgIndex];
    if (!entry || !entry.items || !entry.items[itemIndex]) return;
    const eula = entry.items[itemIndex].payment_details_eula || {};

    const operator = {
      name: eula.name || eula.operatorName || eula.operator?.name,
      operatorKey: eula.operatorKey || eula.operator?.key,
      obfuscateName: eula.obfuscateName || eula.operator?.obfuscateName,
      purpose: eula.purpose || ''
    };

    const ref = this.dialog.open(AgreeComponent, {
      width: '65%',
      backdropClass: 'blurred',
      disableClose: true,
      data: { operator: operator, user: this.currentUser, type: 'eula' },
    });

    ref.afterClosed().subscribe((result) => {
      if (typeof result === 'string' && result.includes('Ok')) {
        entry.items[itemIndex].enabled = true;
        // if all items accepted, mark the message-level accepted flag (for legacy UI)
        if (entry.items.every((it: any) => !!it.enabled)) {
          entry.accepted = true;
        }
      }
    });
  }

  // Handler wired to the checkbox change - opens the EULA dialog for that item
  onEulaCheckboxClick(event: any, msgIndex: number, itemIndex: number) {
    const checked = event.target.checked;
    const item = this.pendingEulaMessages[msgIndex]?.items?.[itemIndex];

    if (!item) {
      return;
    }

    if (checked) {
      // Open PDF only when checking
      this.openEulaFromChat(msgIndex, itemIndex);
    } else {
      // Unchecked - do not open PDF
      item.enabled = false;
      this.pendingEulaMessages[msgIndex].accepted = false;
    }
  }

  // Submit handler: uses the normal chatbot option submit flow (send accepted values)
  submitEulaFromChat(msgIndex: number) {
    const entry = this.pendingEulaMessages[msgIndex];
    if (!entry || !entry.items) return;
    const allAccepted = entry.items.every((it: any) => !!it.enabled);
    if (!allAccepted) return;
    const values = entry.items[0].value;
    const labels = entry.items.map((it: any) => it.label || it.value).join(',<br> ');
    this.lockMessageUiState(msgIndex);
    this.sendMessageToChatbot(values, labels);
  }

  onOptionSelectByLabelValue(label: string, value: string, messageIndex: number) {
    const state = this.getMessageUiState(messageIndex);
    if (state?.isFormSubmitted) return;
    if (!value || this.loading) return;
    if ( value === 'chat_continue_yes' || value === 'chat_continue_no' ) {
      this.handleContinueQuestion(value);
      return;
    }
    this.lockMessageUiState(messageIndex);
    this.sendMessageToChatbot(value, label || value);
  }

  onOptionMultiToggle(messageIndex: number, label: string, value: string, checked: boolean) {
    const state = this.getMessageUiState(messageIndex);
    if (state?.isFormSubmitted) return;

    const selected = this.selectedOptions[messageIndex] || [];
    if (checked) {
      const alreadySelected = selected.some((item) => item.value === value);
      if (alreadySelected) return;
      this.selectedOptions[messageIndex] = [...selected, { label, value }];
      return;
    }

    this.selectedOptions[messageIndex] = selected.filter((item) => item.value !== value);
  }

  submitMultiOptions(messageIndex: number) {
    const state = this.getMessageUiState(messageIndex);
    if (state?.isFormSubmitted) return;

    const selected = this.selectedOptions[messageIndex] || [];
    if (!selected.length || this.loading) return;

    const values = selected.map((item) => item.value).join(',');
    const labels = selected.map((item) => item.label || item.value).join(',<br>');
    this.lockMessageUiState(messageIndex);
    this.sendMessageToChatbot(values, labels);
  }

  onScheduleDateChange(event: any, field: 'specific' | 'recurrent' | 'recurrent-start' | 'recurrent-end' | 'period-start' | 'period-end' | 'range-start' | 'range-end', messageIndex: number) {
        
    const state = this.getMessageUiState(messageIndex);
    if (!state || state.isFormSubmitted) return;

    const rawDateValue = event?.value ?? event?.target?.value ?? event;
    const formatted = this.normalizeDateValue(rawDateValue);
    if (!formatted) return;
    this.clearMessageSubmitError(messageIndex);

    if (field === 'specific') {
      state.activeScheduleMode = 'specific_date';
      state.scheduleDate = formatted;
    } else if (field === 'recurrent') {
      state.activeScheduleMode = 'recurrent';
      state.recurrentDate = formatted;
      state.recurrentStartDate = formatted;
      state.recurrentEndDate = formatted;
    } else if (field === 'recurrent-start') {
      state.activeScheduleMode = 'recurrent';
      state.recurrentStartDate = formatted;
    } else if (field === 'recurrent-end') {
      state.activeScheduleMode = 'recurrent';
      state.recurrentEndDate = formatted;
    } else if (field === 'period-start') {
      state.activeScheduleMode = 'period';
      state.periodStartDate = formatted;
    } else if (field === 'period-end') {
      state.activeScheduleMode = 'period';
      state.periodEndDate = formatted;
    } else if (field === 'range-start') {
      state.dateRangeStart = formatted;
    } else if (field === 'range-end') {
      state.dateRangeEnd = formatted;
    }
    // perform validation after date change
    this.validateScheduleState(messageIndex);
  }

  private bindFlatpickrInputs(): void {
    if (!this.fpInputs?.length) return;

    this.fpInputs.forEach((inputRef) => {
      const input = inputRef.nativeElement;
      if (!input || this.boundFlatpickrInputs.has(input)) return;

      const inputType = input.dataset['fpType'];
      const field = input.dataset['field'] as any;
      const messageIndex = Number(input.dataset['messageIndex']);

      if (!inputType || !field || Number.isNaN(messageIndex)) return;

      const isStartField = ['range-start', 'recurrent-start', 'period-start', 'specific'].includes(field);
      const isEndField = ['range-end', 'recurrent-end', 'period-end'].includes(field);
      const isOrdersRange = field === 'range-start' || field === 'range-end';

      const instance = flatpickr(input, {
        allowInput: false,
        clickOpens: true,
        disableMobile: true,

        dateFormat: inputType === 'date' ? 'd-m-Y' : 'h:i K',
        enableTime: inputType === 'time',
        noCalendar: inputType === 'time',
        time_24hr: false,
        minuteIncrement: 1,

        appendTo: document.body,
        position: 'auto',
        closeOnSelect: true,

        onReady: (_selectedDates, _dateStr, fpInstance) => {
          const state = this.getMessageUiState(messageIndex);

          if (inputType === 'time') {
            const existingValue =
              field === 'start'
                ? state?.scheduleStartSelection
                : state?.scheduleEndSelection;

            if (existingValue) {
              
              const normalized = moment(existingValue, ['HH:mm', 'hh:mm A']).format('hh:mm A');
              fpInstance.setDate(normalized, false, 'h:i K');
              fpInstance.input.value = normalized;
            } else {
              
              const profileTime = this.getProfileCurrentTimeAsDate();
              fpInstance.setDate(profileTime, false);

              setTimeout(() => {
                fpInstance.input.value = '';
              });
            }
          }

          // prevent overlay issues
          const cal = fpInstance.calendarContainer as HTMLElement;
          if (cal) {
            cal.style.zIndex = '99999';
          }
        },
        onOpen: (_selectedDates, _dateStr, fpInstance) => {

          if (inputType === 'date') {

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const state = this.getMessageUiState(messageIndex);

            // =========================
            // ORDERS DATE RANGE
            // =========================
            if (isOrdersRange) {

              // allow past dates
              fpInstance.set('minDate', null);

              // disable future dates
              fpInstance.set('maxDate', today);

              // end date cannot be before start date
              if (field === 'range-end') {

                const startDateStr = state?.dateRangeStart;

                if (startDateStr) {
                  fpInstance.set(
                    'minDate',
                    flatpickr.parseDate(startDateStr, 'Y-m-d')
                  );
                }
              }
            }

            // =========================
            // ALL OTHER DATE PICKERS
            // =========================
            else {

              // disable past dates
              fpInstance.set('minDate', today);

              // allow future dates
              fpInstance.set('maxDate', null);

              // end date min = selected start date
              if (isEndField) {

                let startDate: string | null = null;

                if (field === 'recurrent-end') {
                  startDate = state?.recurrentStartDate;
                }
                else if (field === 'period-end') {
                  startDate = state?.periodStartDate;
                }

                if (startDate) {
                  fpInstance.set(
                    'minDate',
                    flatpickr.parseDate(startDate, 'Y-m-d')
                  );
                }
              }
            }
          }

            requestAnimationFrame(() => {
              (fpInstance as any)._positionCalendar?.();

              const cal = fpInstance.calendarContainer as HTMLElement;
              const rect = input.getBoundingClientRect();

              cal.style.setProperty(
                'top',
                `${rect.bottom + window.scrollY - 80}px`,
                'important'
              );

              cal.style.setProperty(
                'left',
                `${rect.left + window.scrollX}px`,
                'important'
              );
            });
          },
 
        onChange: (selectedDates, _value, fpInstance) => {
          if (!selectedDates || !selectedDates.length) return;

          const dateObj = selectedDates[0];

          // ✅ DATE
          if (inputType === 'date') {
            const formattedDate = fpInstance.formatDate(dateObj, 'd-m-Y');

            this.onScheduleDateChange(
              { target: { value: formattedDate } },
              field,
              messageIndex
            );

            if (field === 'range-start') {
                this.updateEndDateMin(messageIndex, formattedDate);
              }
            return;
          }

          // TIME (SINGLE SOURCE OF TRUTH)
          const formattedTime = moment(dateObj).format('hh:mm A');

          // force input value
          fpInstance.input.value = formattedTime;

          // update state
          if (field === 'start') {
            this.onScheduleStartTimeSet(formattedTime, messageIndex);
          } else {
            this.onScheduleEndTimeSet(formattedTime, messageIndex);
          }

        },

        onClose: () => {
          input.blur();
        }
      });

      this.flatpickrInstances.set(input, instance);
      this.boundFlatpickrInputs.add(input);
    });
  }

  onScheduleStartTimeSet(event: any, messageIndex: number) {
    
    const state = this.getMessageUiState(messageIndex);
    if (!state || state.isFormSubmitted) return;

    const selected = this.formatTimeForDisplay(event);
    if (!selected) return;
    this.clearMessageSubmitError(messageIndex);

    state.scheduleStartSelection = selected;
    const converted = this.onStartTimeChange(selected, this.getStartDateForState(state));
    if (!converted?.utcTime) return;

    state.scheduleStartTime = converted.utcTime;
    // perform validation after start time change
    this.validateScheduleState(messageIndex);
  }

  onScheduleEndTimeSet(event: any, messageIndex: number) {

    const state = this.getMessageUiState(messageIndex);
    if (!state || state.isFormSubmitted) return;

    const selected = this.formatTimeForDisplay(event);
    if (!selected) return;
    this.clearMessageSubmitError(messageIndex);

    state.scheduleEndSelection = selected;
    const converted = this.onEndTimeChange(selected, this.getEndDateForState(state));
    if (!converted?.utcTime) return;

    state.scheduleEndTime = converted.utcTime;
    // perform validation after end time change
    this.validateScheduleState(messageIndex);
  }

  private parseTimeSelection(timeValue: string): Date | null {
    if (!timeValue) return null;

    const normalized = String(timeValue).trim();
    const parsed = moment(normalized, ['hh:mm A', 'h:mm A', 'HH:mm'], true);
    if (!parsed.isValid()) return null;

    const date = new Date();
    date.setHours(parsed.hours(), parsed.minutes(), 0, 0);
    return date;
  }

  private getProfileDefaultTimeHHmm(): string {
    const tz = this.userTimezone || moment.tz.guess();
    return moment.tz(tz).format('hh:mm A'); //  12hr
  }

  private getProfileCurrentTimeAsDate(): Date {
    const now = moment().tz(this.userTimezone);
    const date = new Date();
    date.setHours(now.hours());
    date.setMinutes(now.minutes());
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  submitDateRange(messageIndex: number) {
    
    const state = this.getMessageUiState(messageIndex);
    if (!state || state.isFormSubmitted) return;
    if (this.loading) return;
    // validate UI constraints
    this.validateScheduleState(messageIndex);
    if (!state.isFormValid) return;

    const startDate = this.normalizeDateValue(state.dateRangeStart);
    const endDate = this.normalizeDateValue(state.dateRangeEnd);
    if (!startDate || !endDate) {
      this.setMessageSubmitError(messageIndex, 'Please select both start and end dates.');
      return;
    }

    const startMoment = moment(startDate, 'YYYY-MM-DD', true);
    const endMoment = moment(endDate, 'YYYY-MM-DD', true);
    if (!startMoment.isValid() || !endMoment.isValid()) {
      this.setMessageSubmitError(messageIndex, 'Please select valid dates.');
      return;
    }

    if (endMoment.isBefore(startMoment)) {
      this.setMessageSubmitError(messageIndex, 'End date must be on or after start date.');
      return;
    }

    this.clearMessageSubmitError(messageIndex);

    const payload:any = {
      start_date: startDate,
      end_date: endDate
    };

    const displayText = this.buildDateRangeDisplayMessage(startDate, endDate);
    this.lockMessageUiState(messageIndex);
    this.sendMessageToChatbot(payload, displayText);
  }

  submitSpecificDateSchedule(messageIndex: number) {
    const state = this.getMessageUiState(messageIndex);
    if (!state || state.isFormSubmitted) return;
    if (this.loading) return;
    // validate UI constraints
    this.validateScheduleState(messageIndex);
    if (!state.isFormValid) return;
    if (!state.scheduleDate) {
      this.setMessageSubmitError(messageIndex, 'Please choose a date.');
      return;
    }
    if (!state.scheduleStartSelection || !state.scheduleEndSelection) {
      this.setMessageSubmitError(messageIndex, 'Please select both start and end times.');
      return;
    }

    const utcStart = this.toUtcDateAndTime(state.scheduleDate, state.scheduleStartSelection, false);
    const utcEnd = this.toUtcDateAndTime(state.scheduleDate, state.scheduleEndSelection, true);
    if (!utcStart || !utcEnd) {
      this.setMessageSubmitError(messageIndex, 'Please select valid start and end times.');
      return;
    }
    if (!this.isSelectedEndTimeAfterStartTime(state.scheduleStartSelection, state.scheduleEndSelection)) {
      this.setMessageSubmitError(messageIndex, 'End time must be later than start time.');
      return;
    }

    this.clearMessageSubmitError(messageIndex);

    const localDate = this.normalizeDateValue(state.scheduleDate) || utcStart.date;

    const payload: any = {
      schedule_mode: 'specific_date',
      date: localDate,
      start_time: utcStart.time,
      end_time: utcEnd.time
    };

    const displayText = this.buildSpecificDateDisplayMessage(
      localDate,
      utcStart.time,
      utcEnd.time
    );
    this.lockMessageUiState(messageIndex);
    this.sendMessageToChatbot(payload, displayText);
  }

  submitRecurrentSchedule(messageIndex: number) {
    
    const state = this.getMessageUiState(messageIndex);
    if (!state || state.isFormSubmitted) return;
    if (this.loading) return;
    // validate UI constraints
    this.validateScheduleState(messageIndex);
    if (!state.isFormValid) return;
    if (!state.recurrentStartDate || !state.recurrentEndDate) {
      this.setMessageSubmitError(messageIndex, 'Please select both start and end dates.');
      return;
    }
    if (!state.scheduleStartSelection || !state.scheduleEndSelection) {
      this.setMessageSubmitError(messageIndex, 'Please select both start and end times.');
      return;
    }
    if (!state.recRepeatFrequency) {
      this.setMessageSubmitError(messageIndex, 'Please select a repeat frequency.');
      return;
    }
    if (state.recRepeatFrequency === 'subdaily' && (!state.recSubdailyCount || Number(state.recSubdailyCount) < 1)) {
      this.setMessageSubmitError(messageIndex, 'Subdaily count must be at least 1.');
      return;
    }
    if (state.recRepeatFrequency === 'subdaily' && Number(state.recSubdailyCount) > 8) {
      this.setMessageSubmitError(messageIndex, 'Subdaily Count cannot exceed 8.');
      return;
    }

    const startMoment = moment(state.recurrentStartDate, 'YYYY-MM-DD', true);
    const endMoment = moment(state.recurrentEndDate, 'YYYY-MM-DD', true);
    if (!startMoment.isValid() || !endMoment.isValid()) {
      this.setMessageSubmitError(messageIndex, 'Please select valid dates.');
      return;
    }
    if (endMoment.isBefore(startMoment)) {
      this.setMessageSubmitError(messageIndex, 'End date must be on or after start date.');
      return;
    }

    const utcStart = this.toUtcDateAndTime(state.recurrentStartDate, state.scheduleStartSelection, false);
    const utcEnd = this.toUtcDateAndTime(state.recurrentEndDate, state.scheduleEndSelection, true);
    if (!utcStart || !utcEnd) {
      this.setMessageSubmitError(messageIndex, 'Please select valid start and end times.');
      return;
    }
    if (state.recurrentStartDate === state.recurrentEndDate && !this.isSelectedEndTimeAfterStartTime(state.scheduleStartSelection, state.scheduleEndSelection)) {
      this.setMessageSubmitError(messageIndex, 'End time must be later than start time for same-day schedules.');
      return;
    }

    this.clearMessageSubmitError(messageIndex);

    const localStartDate = this.normalizeDateValue(state.recurrentStartDate) || utcStart.date;
    const localEndDate = this.normalizeDateValue(state.recurrentEndDate) || utcEnd.date;

    const payload: any = {
      schedule_mode: 'recurrent',
      start_date: localStartDate,
      end_date: localEndDate,
      start_time: utcStart.time,
      end_time: utcEnd.time,
      repeat_frequency: state.recRepeatFrequency
    };

    if (state.recRepeatFrequency === 'subdaily' && state.recSubdailyCount) {
      payload.sub_daily_count = Number(state.recSubdailyCount);
    }

    const displayText = `${this.buildScheduleDisplayMessage(
      localStartDate,
      localEndDate,
      utcStart.time,
      utcEnd.time
    )}\nRepeat: ${state.recRepeatFrequency}${state.recRepeatFrequency === 'subdaily' ? '\nSubdaily Count: ' + state.recSubdailyCount : ''}`;
    this.lockMessageUiState(messageIndex);
    this.sendMessageToChatbot(payload, displayText);
  }

  submitPeriodSchedule(messageIndex: number) {
    
    const state = this.getMessageUiState(messageIndex);
    if (!state || state.isFormSubmitted) return;
    if (this.loading) return;
    // validate UI constraints
    this.validateScheduleState(messageIndex);
    if (!state.isFormValid) return;
    if (!state.periodStartDate || !state.periodEndDate) {
      this.setMessageSubmitError(messageIndex, 'Please select both start and end dates.');
      return;
    }
    if (!state.scheduleStartSelection || !state.scheduleEndSelection) {
      this.setMessageSubmitError(messageIndex, 'Please select both start and end times.');
      return;
    }

    const startMoment = moment(state.periodStartDate, 'YYYY-MM-DD', true);
    const endMoment = moment(state.periodEndDate, 'YYYY-MM-DD', true);
    if (!startMoment.isValid() || !endMoment.isValid()) {
      this.setMessageSubmitError(messageIndex, 'Please select valid dates.');
      return;
    }
    if (endMoment.isBefore(startMoment)) {
      this.setMessageSubmitError(messageIndex, 'End date must be on or after start date.');
      return;
    }

    const utcStart = this.toUtcDateAndTime(state.periodStartDate, state.scheduleStartSelection, false);
    const utcEnd = this.toUtcDateAndTime(state.periodEndDate, state.scheduleEndSelection, true);
    if (!utcStart || !utcEnd) {
      this.setMessageSubmitError(messageIndex, 'Please select valid start and end times.');
      return;
    }
    const localStartDate = this.normalizeDateValue(state.periodStartDate) || utcStart.date;
    const localEndDate = this.normalizeDateValue(state.periodEndDate) || utcEnd.date;
    if (localStartDate === localEndDate && !this.isSelectedEndTimeAfterStartTime(state.scheduleStartSelection, state.scheduleEndSelection)) {
      this.setMessageSubmitError(messageIndex, 'End time must be later than start time for same-day schedules.');
      return;
    }

    this.clearMessageSubmitError(messageIndex);

    const payload: any = {
      schedule_mode: 'period',
      start_date: localStartDate,
      end_date: localEndDate,
      start_time: utcStart.time,
      end_time: utcEnd.time
    };

    const displayText = this.buildScheduleDisplayMessage(
      localStartDate,
      localEndDate,
      utcStart.time,
      utcEnd.time
    );
    this.lockMessageUiState(messageIndex);
    this.sendMessageToChatbot(payload, displayText);
  }

  private isSelectedEndTimeAfterStartTime(startSelection: string, endSelection: string): boolean {
    const normalizedStart = this.formatTimeForPayload(startSelection);
    const normalizedEnd = this.formatTimeForPayload(endSelection);
    if (!normalizedStart || !normalizedEnd) return false;
    return this.isEndTimeAfterStartTime(normalizedStart, normalizedEnd);
  }

  private buildDateRangeDisplayMessage(startDate: string, endDate: string): string {
    const s = this.formatDateForDisplayShort(startDate);
    const e = this.formatDateForDisplayShort(endDate);
    return `Start Date: ${s}\nEnd Date: ${e}`;
  }

  private buildScheduleDisplayMessage(startDate: string, endDate: string, startTime: string, endTime: string): string {
    const formattedStartTime = this.formatTimeForDisplay(startTime) || startTime;
    const formattedEndTime = this.formatTimeForDisplay(endTime) || endTime;
    const s = this.formatDateForDisplayShort(startDate);
    const e = this.formatDateForDisplayShort(endDate);
    return `Start Date: ${s}\nEnd Date: ${e}\nStart Time: ${formattedStartTime}\nEnd Time: ${formattedEndTime}`;
  }

  private buildSpecificDateDisplayMessage(date: string, startTime: string, endTime: string): string {
    const d = this.formatDateForDisplayShort(date);
    const formattedStartTime = this.formatTimeForDisplay(startTime) || startTime;
    const formattedEndTime = this.formatTimeForDisplay(endTime) || endTime;
    return `Date: ${d}\nStart Time: ${formattedStartTime}\nEnd Time: ${formattedEndTime}`;
  }

  onStartTimeChange(event: any, date: any) {
    const localStartDate = moment(date);
    const localTime = `${localStartDate.format('YYYY-MM-DD')} ${event}`;
    const localMoment = moment.tz(localTime, 'YYYY-MM-DD HH:mm A', this.userTimezone);
    const utcMoment = localMoment.utc();
    const utcStartDate = new Date(utcMoment.format('YYYY-MM-DD'));
    const utcTime = utcMoment.format('hh:mm A');
    return { utcTime, utcStartDate };
  }

  onEndTimeChange(event: any, date: any) {
    const localStartDate = moment(date);
    const localTime = `${localStartDate.format('YYYY-MM-DD')} ${event}`;
    const localMoment = moment.tz(localTime, 'YYYY-MM-DD HH:mm A', this.userTimezone);
    const utcMoment = localMoment.utc();
    const utcEndDate = new Date(utcMoment.format('YYYY-MM-DD'));
    const utcTime = utcMoment.format('hh:mm A');
    return { utcTime, utcEndDate };
  }

  private toUtcDateAndTime(dateValue: string, timeValue: string, isEnd: boolean): { date: string; time: string } | null {
    const formattedDate = this.normalizeDateValue(dateValue);
    const timeForConversion = this.formatTimeForDisplay(timeValue);
    if (!formattedDate || !timeForConversion) return null;

    const converted = isEnd
      ? this.onEndTimeChange(timeForConversion, formattedDate)
      : this.onStartTimeChange(timeForConversion, formattedDate);

    const convertedDate = isEnd ? (converted as any)?.utcEndDate : (converted as any)?.utcStartDate;
    const utcDate = this.formatDateForPayload((convertedDate?.toISOString?.() || formattedDate));
    const utcTime = this.formatTimeForPayload(converted?.utcTime || '');
    if (!utcDate || !utcTime) return null;

    return { date: utcDate, time: utcTime };
  }

  private getStartDateForState(state: any): string {
    if (state?.activeScheduleMode === 'specific_date') return state.scheduleDate;
    if (state?.activeScheduleMode === 'specific_day') { return moment.tz(this.userTimezone).day(state.specificDay) .format('YYYY-MM-DD');}
    if (state?.activeScheduleMode === 'recurrent') return state.recurrentStartDate || state.recurrentDate;
    if (state?.activeScheduleMode === 'period') return state.periodStartDate;
    return state?.scheduleDate || state?.recurrentStartDate || state?.recurrentDate || state?.periodStartDate || this.normalizeDateValue(this.todayDate) || '';
  }

  private getEndDateForState(state: any): string {
    if (state?.activeScheduleMode === 'specific_date') return state.scheduleDate;
    if (state?.activeScheduleMode === 'recurrent') return state.recurrentEndDate || state.recurrentStartDate || state.recurrentDate;
    if (state?.activeScheduleMode === 'period') return state.periodEndDate || state.periodStartDate;
    return state?.scheduleDate || state?.recurrentEndDate || state?.recurrentStartDate || state?.recurrentDate || state?.periodEndDate || state?.periodStartDate || this.normalizeDateValue(this.todayDate) || '';
  }

  private normalizeDateValue(value: any): string | null {
    if (!value) return null;

    if (moment.isMoment(value)) {
      return value.isValid() ? value.format('YYYY-MM-DD') : null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : moment(value).format('YYYY-MM-DD');
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const strictFormats = ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY', 'DD-MM-YYYY', 'D-M-YYYY', 'MMM D, YYYY'];
      const strictMoment = moment(trimmed, strictFormats, true);
      if (strictMoment.isValid()) return strictMoment.format('YYYY-MM-DD');

      const looseMoment = moment(trimmed);
      return looseMoment.isValid() ? looseMoment.format('YYYY-MM-DD') : null;
    }

    if (value?._d instanceof Date) {
      return Number.isNaN(value._d.getTime()) ? null : moment(value._d).format('YYYY-MM-DD');
    }

    const fallbackMoment = moment(value);
    return fallbackMoment.isValid() ? fallbackMoment.format('YYYY-MM-DD') : null;
  }

  formatDateForPayload(dateValue: string): string {
    const parsedDate = new Date(dateValue);
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatTimeForPayload(timeValue: string): string | null {
    if (!timeValue) return null;

    const normalizedTime = timeValue.trim();
    const twentyFourHourPattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
    const twelveHourPattern = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*([AaPp][Mm])$/;

    const twentyFourHourMatch = normalizedTime.match(twentyFourHourPattern);
    if (twentyFourHourMatch) {
      return `${twentyFourHourMatch[1]}:${twentyFourHourMatch[2]}`;
    }

    const twelveHourMatch = normalizedTime.match(twelveHourPattern);
    if (!twelveHourMatch) return null;

    let hour = Number(twelveHourMatch[1]);
    const minute = twelveHourMatch[2];
    const meridiem = twelveHourMatch[3].toUpperCase();

    if (meridiem === 'PM' && hour !== 12) hour += 12;
    if (meridiem === 'AM' && hour === 12) hour = 0;

    return `${String(hour).padStart(2, '0')}:${minute}`;
  }

  private formatTimeForDisplay(timeValue: string): string | null {
    const formatted = this.formatTimeForPayload(timeValue);
    if (!formatted) return null;
    return moment(formatted, 'HH:mm').format('hh:mm A');
  }

  isEndTimeAfterStartTime(startTime: string, endTime: string): boolean {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    const startTotalMinutes = (startHour * 60) + startMinute;
    const endTotalMinutes = (endHour * 60) + endMinute;
    return endTotalMinutes > startTotalMinutes;
  }

  clearSubmitError(): void {
    if (!this.submitError) return;
    this.submitError = '';
  }

  private isValidTimezone(timezone: string | null | undefined): boolean {
    if (!timezone) return false;
    return !!moment.tz.zone(timezone);
  }

  scrollToBottom(speedTime: number) {
    setTimeout(() => {
      const container = document.querySelector('.messages');
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, speedTime);
  }

  toggleMinimize() {
    this.isMinimized = !this.isMinimized;
    
    // Notify overlay service of minimize state change
    this.chatbotOverlay.setMinimized(this.isMinimized);
    
    if (!this.isMinimized) {
      this.scrollToBottom(500);
    }
    this.saveState();
  }

  clearChat() {
    if (this.messages.length === 0 && !this.sessionId) return;
    this.activeChatSubscription?.unsubscribe();
    this.clearAwaitingState();
    this.loading = false;

    const payload = {
      session_id: this.sessionId || '',
    };

    this.sessionId = '';
    const msg = { ...this.defaultMsg, timestamp: new Date(), formatTime: this.convertTime(new Date()) };
    this.messages = [];
    this.draftMessage = [msg];
    this.chatbotOverlay.clearChatState();

    if(!payload.session_id) return;
    this.apiService.clearUserMessage(payload).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  closeChat() {
    this.chatbotOverlay.clearChatState();
    this.chatbotOverlay.closeChatBot();
  }

  onCloseClick(): void {
    if (!this.messages.length) {
      this.closeChat();
      return;
    }
    this.showCloseConfirm = true;
  }

  closeWithoutEmail(): void {
    this.showCloseConfirm = false;
    this.closeChat();
  }

  async sendMailToUserAndClose(): Promise<void> {
    this.sendingMailToUser = true;
    try {
      const exportedAt: any = this.convertTime(new Date());
      const payload = await this.chatBotHelper.sendConversationToCsm(this.messages, exportedAt);
      this.apiService.sendChatbotUserMailEmailConversation(payload).subscribe({
        next: () => {
          this.sendingMailToUser = false;
          this.mailSentToUser    = true;
          // Show success toast in popover for 2s, then close
          setTimeout(() => {
            this.mailSentToUser = false;
            this.closeChat();
          }, 2000);
        },
        error: () => {
          this.sendingMailToUser = false;
          this.closeChat();
        }
      });
    } catch {
      this.sendingMailToUser = false;
      this.closeChat();
    }
  }

  private saveState(): void {
    const serializableMessages = this.messages.map((m: any) => ({
      sender: m.sender,
      message: m.message,
      uiType: m.uiType ?? null,
      intent: m.intent ?? null,
      intentType: m.intentType ?? null,
      ui: m.ui ?? null,
      timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : (m.timestamp ?? new Date().toISOString()),
      formatTime: m.formatTime,
      uiState: m.uiState ?? null
    }));
    this.chatbotOverlay.saveChatState({
      isMinimized: this.isMinimized,
      sessionId: this.sessionId ?? '',
      messages: serializableMessages
    });
  }

  async sendMail() {
    this.disableMail = true;
    this.loading = true;
    const exportedAt:any = this.convertTime(new Date());
    let payload : any = await this.chatBotHelper.sendConversationToCsm(this.messages, exportedAt);

    this.apiService.sendChatbotContactSupportEmail(payload).subscribe({
        next: () => {
          this.loading = false;
          this.disableMail = true;
          this.showContinueQuestion();
        },
        error: () => {
          this.loading = false;
          this.disableMail = false;
        }
    });
  }

  convertTime(dateTime: any) {
    if (!dateTime) return null;
    return this.utilsService.convertUtcToLocal(dateTime, this.currentUser);
  }

  private updateEndDateMin(messageIndex: number, startDateStr: string) {
    const startDate:any = flatpickr.parseDate(startDateStr, 'Y-m-d');

    this.flatpickrInstances.forEach((instance, input) => {
      const field = input.dataset['field'];
      const index = Number(input.dataset['messageIndex']);

      if (field === 'range-end' && index === messageIndex) {
        instance.set('minDate', startDate);

        // 🔥 also fix invalid already selected end date
        const selected = instance.selectedDates?.[0];
        if (selected && selected < startDate) {
          instance.clear();
        }
      }
    });
  }

  isDayDisabled(dayValue: string, messageIndex: number): boolean {

    const state = this.getMessageUiState(messageIndex);

    if (!state) return true;

    // user current day in profile timezone
    const now = moment.tz(this.userTimezone);

    // convert weekday names to index
    const weekMap: any = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6
    };

    const todayIndex = now.day();

    const targetIndex = weekMap[dayValue.toLowerCase()];

    // disable today + previous days
    return targetIndex <= todayIndex;
  }

  
  onSpecificDaySelect(day: string, messageIndex: number) {
    
    const state = this.getMessageUiState(messageIndex);
    if (!state) return;
    state.activeScheduleMode = 'specific_day';
    state.specificDay = day;
    this.clearMessageSubmitError(messageIndex);
    // reuse validation
    this.validateScheduleState(messageIndex);
  }

  submitSpecificDaySchedule(messageIndex: number) {

    const state = this.getMessageUiState(messageIndex);

    if (!state) return;

    this.validateScheduleState(messageIndex);

    if (!state.isFormValid) return;

    const targetDate = moment().tz(this.userTimezone).day(state.specificDay) .format('YYYY-MM-DD');

    const utcStart = this.toUtcDateAndTime( targetDate, state.scheduleStartSelection, false );

    const utcEnd = this.toUtcDateAndTime( targetDate, state.scheduleEndSelection, true );

    if (!utcStart || !utcEnd) {
      this.setMessageSubmitError(
        messageIndex,
        'Please select valid start/end times.'
      );
      return;
    }

    const payload = {
      schedule_mode: 'specific_day',
      specific_day: state.specificDay,
      start_time: state.scheduleStartTime,
      end_time: state.scheduleEndTime
    };

    const displayText = `Day: ${state.specificDay}
      Start Time: ${state.scheduleStartSelection}
      End Time: ${state.scheduleEndSelection}`;

    this.lockMessageUiState(messageIndex);

    this.sendMessageToChatbot(payload, displayText);
  }

  onTableMultiToggle( messageIndex: number, row: any, checked: boolean ) {

    const state = this.getMessageUiState(messageIndex);
    if (state?.isFormSubmitted) return;

    const selected = this.selectedTableRows[messageIndex] || [];
    if (checked) {

      const exists = selected.some(
        (r) => r.value === row.value
      );

      if (exists) return;

      this.selectedTableRows[messageIndex] = [
        ...selected,
        row
      ];
      
      state.isFormValid = this.selectedTableRows[messageIndex].length > 0;
      return;
    }

    this.selectedTableRows[messageIndex] =
      selected.filter(
        (r) => r.value !== row.value
      );
    state.isFormValid = this.selectedTableRows[messageIndex].length > 0;
  }

  submitTableMultiSelect(messageIndex: number) {

    const state = this.getMessageUiState(messageIndex);

    if (state?.isFormSubmitted) return;

    const selected =
      this.selectedTableRows[messageIndex] || [];

    if (!selected.length || this.loading) return;

    const selectionField =
      this.messages?.[messageIndex]?.ui?.selection_field || 'value';

    const values = selected
      .map((row) => row?.[selectionField])
      .filter(Boolean);

    if (!values.length) return;

    const payload = values.join(',');

    const displayText = values.join(',<br> ');

    this.lockMessageUiState(messageIndex);

    this.sendMessageToChatbot(payload, displayText);
  }

  isSpecificDayDisabled(value: string): boolean {
    if (!value) return false;
    
    if (value.toLowerCase() !== 'specific day') {
      return false;
    }
    
    const now = moment.tz(this.userTimezone);
    return now.day() === 6;
  }

  showContinueQuestion() {
    this.messages.push({
      sender: 'assistant',
      message: `
        The conversation has been sent to the CSM team via email. A copy has also been sent to your registered email address. One of the team members will contact you soon. Do you want to proceed with a different question?
      `,
      uiType: 'clarify',
      disableUserInput: true,
      ui: {
        data: [
          {
            label: 'Yes',
            value: 'chat_continue_yes'
          },
          {
            label: 'No',
            value: 'chat_continue_no'
          }
        ]
      },
      formatTime: this.convertTime(new Date()),
      uiState: {
        isFormSubmitted: false
      }
    });

    this.scrollToBottom(100);
  };

  handleContinueQuestion(value: string) {
    if (value === 'chat_continue_yes') {
      this.clearChat();
      return;
    }

    if (value === 'chat_continue_no') {
      this.closeChat();
    }
  }

  private clearAwaitingState(): void {
    if (this.awaitingRetryTimer) {
      clearTimeout(this.awaitingRetryTimer);
      this.awaitingRetryTimer = null;
    }
    this.isAwaiting = false;
    this.pendingRetryPayload = null;
    this.pendingRetryDisplayText = null;
    this.pendingRetryAttempts = 0;
    this.loading = false;
    this.disableMail = false;
  }
  
  onSubdailyCountChange(messageIndex: number): void {
    this.clearMessageSubmitError(messageIndex);
    this.validateScheduleState(messageIndex);
  }

  trackByMessage(index: number, msg: any): any {
    return msg?.id || msg?.timestamp?.getTime?.() || msg?.timestamp || `${msg?.sender || 'message'}-${index}`;
  }

  trackByColumn(index: number, col: any): any {
    return col?.key || col?.header || index;
  }

  trackByRow(index: number, row: any): any {
    return row?.id || row?._id || row?.uuid || row?.task_id || row?.order_id || index;
  }

  parseMarkdownCell(value: any): SafeHtml {
    if (!value || typeof value !== 'string') return value ?? '';

    const cached = this.markdownCellCache.get(value);
    if (cached) return cached;

    try {
      const raw: any = marked.parse(value);
      const safeHtml = this.sanitizer.bypassSecurityTrustHtml(raw);
      this.markdownCellCache.set(value, safeHtml);
      return safeHtml;
    } catch {
      const safeHtml = this.sanitizer.bypassSecurityTrustHtml(String(value));
      this.markdownCellCache.set(value, safeHtml);
      return safeHtml;
    }
  }
}
